import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { APIContext } from "../../api";
import { formatISTDateTime } from "../../lib/admin-time";

const metrics = [
  ["directories", "Directories"],
  ["directory_listings", "Directory listings"],
  ["pages", "Website pages"],
  ["managed_pages", "Managed pages"],
  ["subscribers", "Newsletter subscribers"],
  ["campaigns_sent", "Campaigns sent"],
  ["prompts", "AI prompts"],
  ["completed_edits", "Completed AI edits"],
];

export default function AiProjectOverview({ projectID, originalBrief }) {
  const { getProjectAiOverview } = useContext(APIContext);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    getProjectAiOverview(projectID)
      .then((result) => { if (current) setOverview(result); })
      .catch(() => { if (current) { setOverview(null); setError(true); } })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
    // APIContext recreates its methods on provider renders; project and refresh drive this request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID, refresh]);

  return (
    <section className="w-full space-y-4 border-t border-slate-200 pt-5" aria-label="AI website overview">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">AI website overview</h2>
          <p className="text-sm text-slate-500">Site activity and the latest builder conversation</p>
        </div>
        <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && <p role="status" className="text-sm text-slate-500">Loading AI website activity…</p>}
      {error && <p role="alert" className="text-sm text-red-700">AI website activity could not be loaded. Use Refresh to try again.</p>}
      {!loading && overview && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {metrics.map(([key, label]) => <div key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{overview.metrics?.[key] == null ? "—" : Number(overview.metrics[key]).toLocaleString()}</p>
          </div>)}
        </div>
        <p className="text-xs text-slate-500">Website pages count saved site routes; managed pages count records in the project page catalog.</p>

        {originalBrief && <div className="rounded-lg border border-purple-100 bg-purple-50/50 p-4">
          <h3 className="text-sm font-semibold text-slate-900">Original website brief</h3>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-700">{originalBrief}</p>
        </div>}

        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Recent AI builder conversation</h3>
          {overview.messages?.length ? (
            <ol className="max-h-[440px] overflow-y-auto space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" aria-label="AI builder conversation">
              {overview.messages.map((message) => {
                const isUser = message.role === "user";
                const canLink = isUser && message.user_id && !message.user_id.startsWith("guest-");
                return <li key={message.id} className={`rounded-lg border p-3 ${isUser ? "bg-white border-slate-200" : "bg-purple-50 border-purple-100"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-medium text-slate-500">
                    <span>{canLink ? <Link to={`/users/${encodeURIComponent(message.user_id)}`} target="_blank" rel="noopener noreferrer" className="text-[#623ce7] hover:underline">User</Link> : isUser ? "Guest user" : "AI builder"}</span>
                    <span>{formatISTDateTime(message.created_at)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-800">{message.content || (message.status === "running" ? "Generating a response…" : "No text response")}{message.truncated ? "…" : ""}</p>
                </li>;
              })}
            </ol>
          ) : <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No AI builder conversation has been recorded for this website.</p>}
          <p className="mt-2 text-xs text-slate-500">Showing up to 30 recent messages.</p>
        </div>
      </>}
    </section>
  );
}
